import { Loader2, Upload } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { ConfirmActionDialog } from "@/components/confirm-action-dialog"
import { AssetVersionsGallery } from "@/components/media/asset-versions-gallery"
import { Button } from "@/components/ui/primitives"
import { useMultiFileUpload } from "@/hooks/use-multi-file-upload"
import { useTranslation } from "@/hooks/use-translation"
import {
  activateLocationReferenceVersion,
  deleteLocationReferenceVersion,
  listLocationReferenceVersions,
  PRIMARY_LOCATION_FACING,
  uploadLocationReferenceVersion,
  type LocationReferenceVersionRow,
  type LocationStorableFacing,
} from "@/lib/locations-api"
import { isAllowedReferenceImageFile, REFERENCE_IMAGE_FILE_ACCEPT } from "@/lib/reference-file-policy"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  locationId: number
  locationName: string
  variantId: number | null
  variantName?: string | null
  facing?: LocationStorableFacing
  allowUpload?: boolean
  onChanged?: () => void
  onCorrect?: (version: LocationReferenceVersionRow) => void
}

export function LocationReferencesGallery({
  open,
  onOpenChange,
  projectId,
  locationId,
  locationName,
  variantId,
  variantName,
  facing = PRIMARY_LOCATION_FACING,
  allowUpload = true,
  onChanged,
  onCorrect,
}: Props) {
  const { t } = useTranslation(["location", "common", "workspace"])
  const [versions, setVersions] = useState<LocationReferenceVersionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await listLocationReferenceVersions(projectId, locationId, variantId, facing)
      setVersions(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [projectId, locationId, variantId, facing])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    void listLocationReferenceVersions(projectId, locationId, variantId, facing)
      .then((rows) => {
        if (!cancelled) setVersions(rows)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, projectId, locationId, variantId, facing])

  const { fileRef, uploading, inputProps } = useMultiFileUpload({
    uploadOne: (file) => uploadLocationReferenceVersion(projectId, locationId, file, variantId, facing),
    onBatchStart: () => setError(null),
    onBatchComplete: async () => {
      await reload()
      onChanged?.()
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
    accept: REFERENCE_IMAGE_FILE_ACCEPT,
    validateFile: (file) => {
      if (!isAllowedReferenceImageFile(file)) {
        throw new Error(t("referenceInputs.unsupportedFileType", { ns: "common" }))
      }
      return true
    },
  })

  const handleActivate = async (versionId: number) => {
    setBusyId(versionId)
    setError(null)
    try {
      await activateLocationReferenceVersion(projectId, locationId, versionId)
      setVersions((list) => list.map((x) => ({ ...x, active: x.id === versionId })))
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  const performDelete = async (versionId: number) => {
    setBusyId(versionId)
    setError(null)
    try {
      await deleteLocationReferenceVersion(projectId, locationId, versionId)
      await reload()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <AssetVersionsGallery
        open={open}
        onOpenChange={onOpenChange}
        title={t("references.title")}
        description={
          <>
            {locationName}
            {variantName ? t("references.variantSuffix", { name: variantName }) : t("references.base")}
          </>
        }
        closeAriaLabel={t("close", { ns: "common" })}
        hint={t("references.hint")}
        toolbar={
          allowUpload ? (
            <div className="flex items-center gap-2">
              <input ref={fileRef} {...inputProps} />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                {t("references.upload")}
              </Button>
            </div>
          ) : undefined
        }
        versions={versions}
        loading={loading}
        error={error}
        busyId={busyId}
        labels={{
          active: t("references.active"),
          noPreview: t("references.noPreview"),
          uploaded: t("references.uploaded"),
          generated: t("references.generated"),
          cleared: t("references.cleared"),
          correct: t("references.correct"),
          delete: t("references.delete"),
          doubleClickActivate: t("references.doubleClickActivate"),
          loading: t("references.loading"),
          empty: t("references.empty"),
          close: t("close", { ns: "common" }),
        }}
        previewAspectClass="aspect-video"
        previewChrome="cover"
        downloadFilename={
          variantId != null
            ? t("locationsPage.variantLightboxTitle", {
                ns: "workspace",
                name: locationName,
                variant: variantName ?? "",
                facing: t(`locationsPage.facing.${facing}Short`, { ns: "workspace", defaultValue: facing }),
              })
            : t("locationsPage.lightboxTitle", {
                ns: "workspace",
                name: locationName,
                facing: t(`locationsPage.facing.${facing}Short`, { ns: "workspace", defaultValue: facing }),
              })
        }
        onActivate={(id) => void handleActivate(id)}
        onDelete={(id) => setPendingDeleteId(id)}
        onCorrect={onCorrect}
      />

      <ConfirmActionDialog
        open={pendingDeleteId != null}
        onOpenChange={(next) => {
          if (!next) setPendingDeleteId(null)
        }}
        message={t("references.deleteConfirm")}
        tone="danger"
        onConfirm={() => {
          const id = pendingDeleteId
          if (id != null) void performDelete(id)
        }}
      />
    </>
  )
}
